import type { GroupLifeTarifacaoLife } from '@/services/group-life';

/**
 * Capitais da aba "Coberturas e Serviços" da análise PME, derivados das vidas
 * tarifadas. Ficam fora do componente para poderem ser testados: o capital
 * total já saiu errado em produção, multiplicando o capital de uma vida pelo
 * tamanho do grupo.
 *
 * A TAXA não mora mais aqui: ela é calculada a partir do prêmio e do capital da
 * própria linha (ver `average-rate.util`), e não de um campo do tarifado.
 *
 * Os mapas são indexados por CÓDIGO e por NOME da cobertura porque o
 * demonstrativo do cálculo (`calculation.breakdown`) traz ora um, ora outro.
 */

/**
 * Capital segurado TOTAL de cada cobertura: soma do capital vida a vida.
 *
 * Antes guardava o capital da primeira vida e o consumidor multiplicava pelo
 * total de vidas — o que só fecha em capital uniforme e errava em capital
 * variável e múltiplo salarial, onde cada vida tem o seu.
 */
export function coverageTotalSumInsured(lives: GroupLifeTarifacaoLife[]) {
  const totals = new Map<string, number>();
  for (const life of lives) {
    for (const coverage of life.coverages) {
      if (coverage.sumInsured == null) continue;
      for (const key of [coverage.code, coverage.name]) {
        totals.set(key, (totals.get(key) ?? 0) + coverage.sumInsured);
      }
    }
  }
  return totals;
}

/**
 * Capital da apólice por cobertura: o do estipulante master mais o de cada
 * subestipulante. A lista de estipulantes mostra cada mapa sozinho; a aba de
 * coberturas precisa do total.
 */
export function sumCoverageSumInsured(
  groups: Array<Record<string, number> | undefined>
): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const group of groups) {
    if (!group) continue;
    for (const [code, value] of Object.entries(group)) {
      if (!(value > 0)) continue;
      totals[code] = (totals[code] ?? 0) + value;
    }
  }
  return totals;
}

/**
 * Capital POR VIDA de cada cobertura — é o que a coluna "Valor por Segurado"
 * da tabela de serviços pede. Guarda o primeiro valor visto: nas assistências o
 * benefício é o mesmo para todo o grupo.
 */
export function coverageUnitSumInsured(lives: GroupLifeTarifacaoLife[]) {
  const values = new Map<string, number>();
  for (const life of lives) {
    for (const coverage of life.coverages) {
      if (coverage.sumInsured == null) continue;
      for (const key of [coverage.code, coverage.name]) {
        if (!values.has(key)) values.set(key, coverage.sumInsured);
      }
    }
  }
  return values;
}
